// Two-step OTP signup:
//   Step 1: visitor enters name / email / phone → server emails a 6-digit code.
//   Step 2: visitor types the code → server creates the customer account and
//           sets the auth cookie in one round-trip; we redirect them onward.
// No password is collected — passwordless email-OTP signup. Future sign-ins
// will follow the same OTP flow once we wire it.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OtpInput } from '../components/OtpInput';
import { ShieldCheck, AtSign, Phone, User, ArrowRight, KeyRound } from 'lucide-react';

const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(' ');
  return typeof d === 'string' ? d : (e?.message || 'Something went wrong');
};

const Signup = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { otpStart, otpVerify } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [cooldown, setCooldown] = useState(0); // seconds until "Resend" is allowed

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendCode = async (e) => {
    e?.preventDefault?.();
    setErr('');
    setBusy(true);
    try {
      await otpStart(form);
      setStep(2);
      setCooldown(30);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e, typed) => {
    e?.preventDefault?.();
    const code = typed || otp;
    if (busy) return;
    if (code.length < 6) { setErr('Enter all 6 digits of the code from your email.'); return; }
    setErr('');
    setBusy(true);
    try {
      await otpVerify({ email: form.email, otp: code });
      nav(sp.get('next') || '/sell-your-car', { replace: true });
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 sm:pt-32 pb-12 bg-[#0b1722] text-white">
      <div className="absolute inset-x-0 top-0 h-[55vh] -z-0 bg-[radial-gradient(60%_50%_at_80%_15%,rgba(20,184,166,0.20),transparent_60%)]" />
      <div className="relative max-w-md mx-auto px-4 sm:px-6">
        <Link to="/" className="text-xs text-teal-300 inline-flex items-center gap-1 mb-6">
          <ShieldCheck size={12} /> UNISOL GLOBAL
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
          {step === 1 ? 'Create your account' : 'Verify your email'}
        </h1>
        <p className="text-slate-300 mt-2 text-sm leading-relaxed">
          {step === 1
            ? 'Sell your car on Unisol — list, manage and respond to buyers in minutes.'
            : <>We sent a 6-digit code to <b className="text-white break-all">{form.email}</b>. Enter it below.</>}
        </p>

        {/* Step indicator (mobile-friendly) */}
        <div className="flex items-center gap-2 mt-5">
          <Pill active={step >= 1} label="Your details" num={1} />
          <div className="flex-1 h-px bg-white/15" />
          <Pill active={step >= 2} label="Verify" num={2} />
        </div>

        <form onSubmit={step === 1 ? sendCode : verify}
              className="mt-6 bg-white/[0.04] border border-white/10 rounded-2xl p-5 sm:p-6 space-y-4"
              data-testid="signup-form">
          {step === 1 ? (
            <>
              <Field icon={User} label="Your name" value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Rohan Kabra" required testid="signup-name" />
              <Field icon={AtSign} label="Email" type="email" inputMode="email" value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                placeholder="rohan@example.com" required testid="signup-email" />
              <Field icon={Phone} label="Phone" type="tel" inputMode="tel" value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                placeholder="+91 98765 43210" required testid="signup-phone" />
            </>
          ) : (
            <OtpInput value={otp} onChange={setOtp} testid="signup-otp" boxTestid="otp" onComplete={(c) => verify(null, c)} />
          )}

          {err && (
            <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs rounded-lg p-3"
                 data-testid="signup-error">{err}</div>
          )}

          <button type="submit" disabled={busy}
            className="w-full bg-teal-400 hover:bg-teal-300 disabled:opacity-60 text-slate-900 font-semibold py-3 rounded-lg text-sm inline-flex items-center justify-center gap-2 active:scale-[0.99]"
            data-testid={step === 1 ? 'signup-send-otp' : 'signup-verify'}>
            {busy
              ? (step === 1 ? 'Sending code…' : 'Verifying…')
              : (step === 1 ? <>Send verification code <ArrowRight size={14} /></> : <>Create my account <ArrowRight size={14} /></>)}
          </button>

          {step === 2 && (
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => { setStep(1); setOtp(''); setErr(''); }}
                className="text-slate-400 hover:text-white" data-testid="signup-back">
                ← Change email
              </button>
              <button type="button" disabled={cooldown > 0 || busy}
                onClick={() => { setOtp(''); sendCode(); }}
                className="text-teal-300 hover:text-teal-200 disabled:text-slate-500"
                data-testid="signup-resend">
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          )}

          <p className="text-xs text-slate-400 text-center pt-1">
            Already have an account?{' '}
            <Link to="/login" className="text-teal-300 hover:underline">Sign in</Link>
          </p>
        </form>

        <p className="text-[11px] text-slate-500 text-center mt-5 leading-relaxed px-3">
          By signing up you agree to our terms — your contact info is shown to interested buyers only.
        </p>
      </div>
    </div>
  );
};

const Pill = ({ active, label, num }) => (
  <div className={`inline-flex items-center gap-1.5 ${active ? 'text-white' : 'text-slate-300'}`}>
    <span className={`w-5 h-5 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${
      active ? 'bg-teal-400 text-slate-900' : 'bg-white/10 text-slate-400'}`}>{num}</span>
    <span className="text-[11px] uppercase tracking-wider hidden sm:inline">{label}</span>
  </div>
);

const Field = ({ icon: Icon, label, value, onChange, placeholder, type = 'text', inputMode, required, testid }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-300 mb-1">{label}{required && ' *'}</span>
    <div className="relative">
      <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input
        type={type} inputMode={inputMode} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        className="w-full bg-slate-900/60 border border-white/15 text-white placeholder-slate-500 rounded-lg pl-9 pr-3 py-3 text-base sm:text-sm outline-none focus:ring-2 focus:ring-teal-400"
        data-testid={testid}
      />
    </div>
  </label>
);

export default Signup;
